import { sha256 } from 'js-sha256';

/**
 * Serializes a confirm-handshake payload, containing the encrypted Kyber ciphertext
 * and a SHA-256 hash of the receiver's Kyber public key.
 *
 * The payload consists of:
 * - 1 byte: protocol version (currently 1)
 * - 2 bytes: ciphertext length (big-endian)
 * - N bytes: Kyber ciphertext
 * - 32 bytes: SHA-256 hash of receiver's Kyber public key
 *
 * This hash is used by the recipient to identify the correct Kyber private key 
 * to use for decapsulation, which is useful when multiple handshake attempts 
 * are pending simultaneously.
 *
 * @param ciphertext - The Kyber ciphertext to send (output of encapsulation)
 * @param receiverKyberPublicKey - The receiver's Kyber public key used during encapsulation
 * @returns Serialized Uint8Array payload
 */
export async function serializeHandshakeConfirmPayload(
  ciphertext: Uint8Array, 
  kyberPublicKey: Uint8Array,
): Promise<Uint8Array> {
  const version = 1;
  const ciphertextLength = ciphertext.length;

  if (ciphertextLength > 0xffff) {
    throw new Error(`Ciphertext too long: ${ciphertextLength} bytes`);
  }
  
  const kyberPublicKeyHash = new Uint8Array(await sha256.array(kyberPublicKey)); // Uint8Array(32)

  const buffer = new Uint8Array(1 + 2 + ciphertextLength + kyberPublicKeyHash.length);
  buffer[0] = version;
  buffer[1] = (ciphertextLength >> 8) & 0xff;
  buffer[2] = ciphertextLength & 0xff;
  buffer.set(ciphertext, 3);
  buffer.set(kyberPublicKeyHash, 3 + ciphertextLength);

  return buffer;
}